/* global axios */

import ApiClient from './ApiClient';

class IntegrationsAPI extends ApiClient {
  constructor() {
    super('integrations/apps', { accountScoped: true });
  }

  connectSlack(code) {
    return axios.post(`${this.baseUrl()}/integrations/slack`, { code });
  }

  updateSlack({ referenceId }) {
    return axios.patch(`${this.baseUrl()}/integrations/slack`, {
      reference_id: referenceId,
    });
  }

  listAllSlackChannels() {
    return axios.get(`${this.baseUrl()}/integrations/slack/list_all_channels`);
  }

  delete(integrationId) {
    return axios.delete(`${this.baseUrl()}/integrations/${integrationId}`);
  }

  createHook(hookData) {
    return axios.post(`${this.baseUrl()}/integrations/hooks`, hookData);
  }

  /**
   * Updates an existing integration hook.
   *
   * @param {number|string} hookId Integration hook id.
   * @param {Object} hookData Hook update payload.
   * @returns {Promise} Axios response with updated hook payload.
   */
  updateHook(hookId, hookData) {
    return axios.patch(
      `${this.baseUrl()}/integrations/hooks/${hookId}`,
      hookData
    );
  }

  deleteHook(hookId) {
    return axios.delete(`${this.baseUrl()}/integrations/hooks/${hookId}`);
  }

  /**
   * Starts Shopify OAuth for an account-owned custom distribution app.
   *
   * @param {Object} payload Shopify connection payload.
   * @param {string} payload.shopDomain Store myshopify.com domain.
   * @param {string} payload.clientId Shopify app client ID.
   * @param {string} payload.clientSecret Shopify app client secret.
   * @returns {Promise} Axios response containing the Shopify authorization URL.
   */
  connectShopify({ shopDomain, clientId, clientSecret }) {
    return axios.post(`${this.baseUrl()}/integrations/shopify/auth`, {
      shop_domain: shopDomain,
      client_id: clientId,
      client_secret: clientSecret,
    });
  }

  /**
   * Fetches dashboard-safe Shopify commerce connection state.
   *
   * @returns {Promise} Axios response with Shopify connection status.
   */
  getShopifyStatus() {
    return axios.get(`${this.baseUrl()}/integrations/shopify/status`);
  }

  /**
   * Runs Shopify GraphQL connection test and refreshes stored shop metadata.
   *
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  testShopify() {
    return axios.post(`${this.baseUrl()}/integrations/shopify/test`);
  }

  /**
   * Enqueues a Shopify product, variant, and inventory bulk snapshot.
   *
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  syncShopifyCatalog() {
    return axios.post(`${this.baseUrl()}/integrations/shopify/sync_catalog`);
  }

  /**
   * Enqueues a Shopify locations bulk snapshot.
   *
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  syncShopifyLocations() {
    return axios.post(`${this.baseUrl()}/integrations/shopify/sync_locations`);
  }

  /**
   * Enqueues a targeted Shopify catalog refresh by provider search query.
   *
   * @param {Object} payload Targeted refresh payload.
   * @param {string} payload.operationType Shopify operation type to refresh.
   * @param {string} [payload.query] Shopify search query, for example a SKU or location filter.
   * @param {string} [payload.gid] Explicit Shopify Product, ProductVariant, or Location GID.
   * @param {number} [payload.pageSize] Admin GraphQL page size.
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  targetedShopifyResync({ operationType, query, gid, pageSize }) {
    return axios.post(
      `${this.baseUrl()}/integrations/shopify/targeted_resync`,
      {
        operation_type: operationType,
        query,
        gid,
        page_size: pageSize,
      }
    );
  }

  /**
   * Enqueues CRM event enrichment replay using the current Shopify catalog.
   *
   * @param {Object} [payload] Replay selector payload.
   * @param {string} [payload.eventType] CRM event type to replay.
   * @param {string} [payload.matchStatus] Existing Shopify enrichment match status to replay.
   * @param {string} [payload.since] Lower occurrence date bound.
   * @param {string} [payload.sku] Parsed product SKU selector.
   * @param {string} [payload.productGid] Parsed Shopify product GID selector.
   * @param {string} [payload.variantGid] Parsed Shopify variant GID selector.
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  replayShopifyEnrichment({
    eventType,
    matchStatus,
    since,
    sku,
    productGid,
    variantGid,
  } = {}) {
    return axios.post(
      `${this.baseUrl()}/integrations/shopify/enrichment/replay`,
      {
        event_type: eventType,
        match_status: matchStatus,
        since,
        sku,
        product_gid: productGid,
        variant_gid: variantGid,
      }
    );
  }

  /**
   * Requests cancellation for an active Shopify bulk operation.
   *
   * @param {number|string} bulkOperationId Local Shopify bulk operation id.
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  cancelShopifyBulkOperation(bulkOperationId) {
    return axios.post(
      `${this.baseUrl()}/integrations/shopify/bulk_operations/${bulkOperationId}/cancel`
    );
  }

  /**
   * Queues provider-state refresh for an active Shopify bulk operation.
   *
   * @param {number|string} bulkOperationId Local Shopify bulk operation id.
   * @returns {Promise} Axios response with refreshed Shopify connection status.
   */
  refreshShopifyBulkOperation(bulkOperationId) {
    return axios.post(
      `${this.baseUrl()}/integrations/shopify/bulk_operations/${bulkOperationId}/refresh`
    );
  }

  /**
   * Disconnects the account-scoped Shopify commerce integration.
   *
   * @returns {Promise} Axios response with disconnected Shopify status.
   */
  disconnectShopify() {
    return axios.delete(`${this.baseUrl()}/integrations/shopify`);
  }

  /**
   * Explicitly purges the locally cached Shopify catalog rows.
   *
   * @returns {Promise} Axios response with purge counts and refreshed status.
   */
  purgeShopifyCatalog() {
    return axios.delete(`${this.baseUrl()}/integrations/shopify/purge`);
  }

  /**
   * Searches the local account-scoped Shopify product cache.
   *
   * @param {Object} [payload] Search payload.
   * @param {string} [payload.query] Product, SKU, barcode, vendor, or type search query.
   * @param {number} [payload.limit] Maximum products to return.
   * @param {number} [payload.page] Page number.
   * @param {string} [payload.externalLocationId] Shopify Location GID to filter stock by.
   * @param {boolean} [payload.inStockOnly] Whether to return only products with stock.
   * @returns {Promise} Axios response with cached Shopify products and pagination metadata.
   */
  searchShopifyProducts({
    query = '',
    limit = 20,
    page,
    externalLocationId,
    inStockOnly,
  } = {}) {
    return axios.get(`${this.baseUrl()}/commerce/shopify/products`, {
      params: {
        q: query,
        limit,
        page,
        external_location_id: externalLocationId,
        in_stock_only: inStockOnly,
      },
    });
  }

  /**
   * Fetches Klaviyo connection state and profile sync summary.
   *
   * @returns {Promise} Axios response with Klaviyo status payload.
   */
  getKlaviyoStatus() {
    return axios.get(`${this.baseUrl()}/integrations/klaviyo/status`);
  }

  /**
   * Tests the stored Klaviyo API key.
   *
   * @returns {Promise} Axios response with refreshed Klaviyo status payload.
   */
  testKlaviyo() {
    return axios.post(`${this.baseUrl()}/integrations/klaviyo/test`);
  }

  /**
   * Enqueues a Klaviyo profile backfill.
   *
   * @returns {Promise} Axios response with refreshed Klaviyo status payload.
   */
  backfillKlaviyo() {
    return axios.post(`${this.baseUrl()}/integrations/klaviyo/backfill`);
  }

  /**
   * Enqueues one manual Klaviyo Events API ingestion page.
   *
   * @returns {Promise} Axios response with refreshed Klaviyo status payload.
   */
  syncKlaviyoEvents() {
    return axios.post(`${this.baseUrl()}/integrations/klaviyo/events/sync`);
  }

  /**
   * Enqueues raw Klaviyo event replay by parser selector.
   *
   * @param {Object} selectorPayload Replay selector payload.
   * @returns {Promise} Axios response with refreshed Klaviyo status payload.
   */
  replayKlaviyoEvents(selectorPayload) {
    return axios.post(
      `${this.baseUrl()}/integrations/klaviyo/events/replay`,
      selectorPayload
    );
  }

  /**
   * Disconnects Klaviyo and removes local CRM sync state.
   *
   * @returns {Promise} Axios response with disconnected Klaviyo status payload.
   */
  disconnectKlaviyo() {
    return axios.delete(`${this.baseUrl()}/integrations/klaviyo`);
  }

  /**
   * Updates Klaviyo two-way sync policy settings.
   *
   * @param {Object} settingsPayload Two-way sync settings payload.
   * @returns {Promise} Axios response with refreshed Klaviyo status payload.
   */
  updateKlaviyoSettings(settingsPayload) {
    return axios.patch(
      `${this.baseUrl()}/integrations/klaviyo/settings`,
      settingsPayload
    );
  }

  /**
   * Fetches the cached Klaviyo profile for a contact.
   *
   * @param {number|string} contactId Contact id in the current account.
   * @returns {Promise} Axios response with contact CRM profile payload.
   */
  getKlaviyoContactProfile(contactId) {
    return axios.get(`${this.baseUrl()}/contacts/${contactId}/crm_profile`);
  }

  /**
   * Fetches the account-level CRM events activity feed.
   *
   * @param {Object} params Pagination and date filter params.
   * @returns {Promise} Axios response with CRM events feed payload.
   */
  getCrmEvents(params = {}) {
    return axios.get(`${this.baseUrl()}/crm/events`, { params });
  }

  /**
   * Creates an operator-originated CRM event.
   *
   * @param {Object} eventPayload Manual CRM event payload.
   * @returns {Promise} Axios response with created CRM event payload.
   */
  createCrmEvent(eventPayload) {
    return axios.post(`${this.baseUrl()}/crm/events`, eventPayload);
  }

  /**
   * Captures an explicit Klaviyo marketing consent action for a contact.
   *
   * @param {number|string} contactId Contact id in the current account.
   * @param {Object} consentPayload Consent action payload.
   * @returns {Promise} Axios response with refreshed contact CRM profile payload.
   */
  captureKlaviyoSubscriptionConsent(contactId, consentPayload) {
    return axios.post(
      `${this.baseUrl()}/contacts/${contactId}/crm_profile/subscription_consent`,
      consentPayload
    );
  }
}

export default new IntegrationsAPI();
